class MonteKitManager {
  constructor(section) {
    this.section = section;
    this.minItems = parseInt(section.dataset.minItems) || 4;
    this.maxItems = parseInt(section.dataset.maxItems) || 12;
    this.maxSlots = parseInt(section.dataset.maxSlots) || this.maxItems;
    this.basePrice = parseInt(section.dataset.basePrice) || 0;
    this.selectedItems = new Map();
    this.totalQuantity = 1;
    this.slots = [];

    this.init();
  }

  init() {
    this.setupElements();
    this.setupEventListeners();
    this.updateSummary();
    this.initializeSlots();
  }

  setupElements() {
    // Product controls
    this.productCards = this.section.querySelectorAll('.monte-kit__product-card');
    this.qtyInputs = this.section.querySelectorAll('.monte-kit__qty-input');

    // Desktop summary
    this.slotsGrid = this.section.querySelector('.monte-kit__slots-grid');
    this.slotElements = this.section.querySelectorAll('.monte-kit__slot');
    this.totalPriceEl = this.section.querySelector('.monte-kit__total-price');
    this.addToCartBtn = this.section.querySelector('.monte-kit__add-to-cart');

    // Mobile summary
    this.totalPriceMobileEl = this.section.querySelector('.monte-kit__total-price-mobile');
    this.addToCartMobileBtn = this.section.querySelector('.monte-kit__add-to-cart-mobile');
    this.selectedCountEl = this.section.querySelector('.monte-kit__selected-count');
    this.summaryToggle = this.section.querySelector('.monte-kit__summary-mobile-toggle');
    this.drawer = this.section.querySelector('.monte-kit__drawer');
    this.drawerClose = this.section.querySelector('.monte-kit__drawer-close');
    this.drawerOverlay = this.section.querySelector('.monte-kit__drawer-overlay');
    this.drawerProductsList = this.section.querySelector('.monte-kit__drawer-products-list');
    this.drawerTotalPrice = this.section.querySelector('.monte-kit__drawer-total-price');
    this.drawerAddCart = this.section.querySelector('.monte-kit__drawer-add-cart');

    // Quantity controls for kit
    this.qtyDisplays = this.section.querySelectorAll('.monte-kit__qty-display');
    this.qtyDecreaseButtons = this.section.querySelectorAll('.monte-kit__qty-decrease');
    this.qtyIncreaseButtons = this.section.querySelectorAll('.monte-kit__qty-increase');

    // Product Info Drawer
    this.productInfoDrawer = this.section.querySelector('.monte-kit__product-info-drawer');
    this.productInfoDrawerClose = this.section.querySelector('.monte-kit__product-info-drawer-close');
    this.productInfoDrawerOverlay = this.section.querySelector('.monte-kit__product-info-drawer-overlay');
    this.productInfoSlideshow = this.section.querySelector('.monte-kit__product-info-slideshow');
    this.productInfoTitle = this.section.querySelector('.monte-kit__product-info-product-title');
    this.productInfoDescription = this.section.querySelector('.monte-kit__product-info-description');
    this.productInfoPrice = this.section.querySelector('.monte-kit__product-info-price');
  }

  initializeSlots() {
    this.slotElements.forEach((slot, index) => {
      this.slots[index] = {
        element: slot,
        variantId: null,
        isEmpty: true
      };
    });
  }

  setupEventListeners() {
    // Product quantity controls
    this.productCards.forEach(card => {
      const minusBtn = card.querySelector('.monte-kit__qty-minus');
      const plusBtn = card.querySelector('.monte-kit__qty-plus');
      const input = card.querySelector('.monte-kit__qty-input');

      minusBtn?.addEventListener('click', () => this.updateQuantity(input, -1));
      plusBtn?.addEventListener('click', () => this.updateQuantity(input, 1));
    });

    // Slot remove buttons
    this.slotElements.forEach((slot, index) => {
      const removeBtn = slot.querySelector('.monte-kit__slot-remove');
      removeBtn?.addEventListener('click', () => this.removeFromSlot(index));
    });

    // Kit quantity controls
    this.qtyDecreaseButtons.forEach(btn => {
      btn.addEventListener('click', () => this.updateKitQuantity(-1));
    });

    this.qtyIncreaseButtons.forEach(btn => {
      btn.addEventListener('click', () => this.updateKitQuantity(1));
    });

    // Add to cart buttons
    [this.addToCartBtn, this.addToCartMobileBtn, this.drawerAddCart].forEach(btn => {
      btn?.addEventListener('click', () => this.addToCart());
    });

    // Mobile drawer controls
    this.summaryToggle?.addEventListener('click', () => this.toggleDrawer());
    this.drawerClose?.addEventListener('click', () => this.closeDrawer());
    this.drawerOverlay?.addEventListener('click', () => this.closeDrawer());

    // Drawer toggle products
    const toggleProductsBtn = this.section.querySelector('.monte-kit__drawer-toggle-products');
    toggleProductsBtn?.addEventListener('click', (e) => {
      const productsSection = this.section.querySelector('.monte-kit__drawer-products');
      const isVisible = productsSection.style.display !== 'none';
      productsSection.style.display = isVisible ? 'none' : 'block';
      e.target.textContent = isVisible ? 'MOSTRAR PRODUTOS ▼' : 'OCULTAR PRODUTOS ▲';
    });

    // Product Info buttons
    const infoButtons = this.section.querySelectorAll('.monte-kit__product-info-btn');
    infoButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openProductInfoDrawer(btn);
      });
    });

    // Product Info Drawer close controls
    this.productInfoDrawerClose?.addEventListener('click', () => this.closeProductInfoDrawer());
    this.productInfoDrawerOverlay?.addEventListener('click', () => this.closeProductInfoDrawer());
  }

  updateQuantity(input, delta) {
    const currentValue = parseInt(input.value) || 0;
    const newValue = Math.max(0, currentValue + delta);
    const totalSelected = this.getTotalSelectedItems();
    const potentialTotal = totalSelected - currentValue + newValue;

    if (potentialTotal > this.maxItems && delta > 0) {
      this.showWarning(`Você pode adicionar no máximo ${this.maxItems} produtos.`);
      return;
    }

    input.value = newValue;

    const variantId = input.dataset.variantId;
    const price = parseInt(input.dataset.variantPrice);
    const title = input.dataset.productTitle;
    const variantTitle = input.dataset.variantTitle;
    const image = input.dataset.productImage;

    if (newValue > 0) {
      this.selectedItems.set(variantId, {
        quantity: newValue,
        price: price,
        title: title,
        variantTitle: variantTitle,
        image: image,
        variantId: variantId
      });
    } else {
      this.selectedItems.delete(variantId);
      this.removeFromAllSlots(variantId);
    }

    // Update slots
    this.updateSlots(variantId, currentValue, newValue);

    // Update minus button state
    const card = input.closest('.monte-kit__product-card');
    const minusBtn = card.querySelector('.monte-kit__qty-minus');
    minusBtn.disabled = newValue === 0;

    this.updateSummary();
  }

  updateSlots(variantId, oldQuantity, newQuantity) {
    const difference = newQuantity - oldQuantity;

    if (difference > 0) {
      // Add to slots
      for (let i = 0; i < difference; i++) {
        this.addToNextEmptySlot(variantId);
      }
    } else if (difference < 0) {
      // Remove from slots
      for (let i = 0; i < Math.abs(difference); i++) {
        this.removeFromLastSlot(variantId);
      }
    }
  }

  addToNextEmptySlot(variantId) {
    const item = this.selectedItems.get(variantId);
    if (!item) return;

    for (let i = 0; i < this.maxSlots; i++) {
      if (this.slots[i].isEmpty) {
        const slot = this.slots[i];
        slot.element.classList.add('filled');
        slot.variantId = variantId;
        slot.isEmpty = false;

        // Add product image
        const contentEl = slot.element.querySelector('.monte-kit__slot-content');
        if (item.image) {
          contentEl.innerHTML = `<img src="${item.image}" alt="${item.title}" class="monte-kit__slot-image" />`;
        } else {
          contentEl.innerHTML = `<div class="monte-kit__slot-placeholder">${item.title.charAt(0)}</div>`;
        }

        // Show remove button
        const removeBtn = slot.element.querySelector('.monte-kit__slot-remove');
        removeBtn.style.display = 'flex';

        // Add animation
        slot.element.style.animation = 'fadeIn 0.3s ease';
        setTimeout(() => {
          slot.element.style.animation = '';
        }, 300);

        break;
      }
    }
  }

  removeFromLastSlot(variantId) {
    for (let i = this.maxSlots - 1; i >= 0; i--) {
      if (!this.slots[i].isEmpty && this.slots[i].variantId === variantId) {
        this.clearSlot(i);
        break;
      }
    }
  }

  removeFromAllSlots(variantId) {
    for (let i = 0; i < this.maxSlots; i++) {
      if (this.slots[i].variantId === variantId) {
        this.clearSlot(i);
      }
    }
  }

  removeFromSlot(slotIndex) {
    const slot = this.slots[slotIndex];
    if (slot.isEmpty) return;

    const variantId = slot.variantId;
    const input = this.section.querySelector(`[data-variant-id="${variantId}"]`);

    if (input) {
      const currentValue = parseInt(input.value) || 0;
      if (currentValue > 0) {
        input.value = currentValue - 1;

        if (input.value === '0') {
          this.selectedItems.delete(variantId);
          const card = input.closest('.monte-kit__product-card');
          const minusBtn = card.querySelector('.monte-kit__qty-minus');
          minusBtn.disabled = true;
        } else {
          const item = this.selectedItems.get(variantId);
          if (item) {
            item.quantity = parseInt(input.value);
          }
        }
      }
    }

    this.clearSlot(slotIndex);
    this.updateSummary();
  }

  clearSlot(slotIndex) {
    const slot = this.slots[slotIndex];
    slot.element.classList.remove('filled');
    slot.variantId = null;
    slot.isEmpty = true;

    const contentEl = slot.element.querySelector('.monte-kit__slot-content');
    contentEl.innerHTML = '<span class="monte-kit__slot-plus">+</span>';

    const removeBtn = slot.element.querySelector('.monte-kit__slot-remove');
    removeBtn.style.display = 'none';
  }

  getTotalSelectedItems() {
    let total = 0;
    this.selectedItems.forEach(item => {
      total += item.quantity;
    });
    return total;
  }

  updateSummary() {
    const totalItems = this.getTotalSelectedItems();
    let totalPrice = this.basePrice;

    this.selectedItems.forEach(item => {
      totalPrice += item.price * item.quantity;
    });

    totalPrice *= this.totalQuantity;

    // Update desktop summary
    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = this.formatMoney(totalPrice);
      this.totalPriceEl.dataset.total = totalPrice;
    }

    // Update mobile summary
    if (this.totalPriceMobileEl) {
      this.totalPriceMobileEl.textContent = this.formatMoney(totalPrice);
    }

    if (this.selectedCountEl) {
      this.selectedCountEl.textContent = totalItems;
    }

    // Update drawer
    this.updateDrawerProducts();
    if (this.drawerTotalPrice) {
      this.drawerTotalPrice.textContent = this.formatMoney(totalPrice);
    }

    // Enable/disable add to cart buttons
    const canAddToCart = totalItems >= this.minItems && totalItems <= this.maxItems;
    [this.addToCartBtn, this.addToCartMobileBtn, this.drawerAddCart].forEach(btn => {
      if (btn) btn.disabled = !canAddToCart;
    });

    // Show warning if needed
    if (totalItems > 0 && totalItems < this.minItems) {
      this.showInfo(`Adicione mais ${this.minItems - totalItems} produto(s) para continuar.`);
    } else if (totalItems === this.maxItems) {
      this.showInfo(`Você atingiu o máximo de ${this.maxItems} produtos.`);
    } else {
      this.hideInfo();
    }
  }

  updateDrawerProducts() {
    if (!this.drawerProductsList) return;

    this.drawerProductsList.innerHTML = '';

    this.selectedItems.forEach(item => {
      const productEl = document.createElement('div');
      productEl.className = 'monte-kit__drawer-item';
      productEl.innerHTML = `
        <div class="monte-kit__drawer-item-image">
          ${item.image ? `<img src="${item.image}" alt="${item.title}">` : ''}
        </div>
        <div class="monte-kit__drawer-item-info">
          <h4>${item.title}</h4>
          <p>${this.formatMoney(item.price)}</p>
        </div>
        <div class="monte-kit__drawer-item-qty">
          <span>${item.quantity}</span>
        </div>
      `;
      this.drawerProductsList.appendChild(productEl);
    });
  }

  updateKitQuantity(delta) {
    const newQuantity = Math.max(1, Math.min(10, this.totalQuantity + delta));
    if (newQuantity === this.totalQuantity) return;

    this.totalQuantity = newQuantity;

    // Update all quantity displays
    this.qtyDisplays.forEach(display => {
      display.textContent = newQuantity;
    });

    // Update button states
    this.qtyDecreaseButtons.forEach(btn => {
      btn.disabled = newQuantity === 1;
    });

    this.qtyIncreaseButtons.forEach(btn => {
      btn.disabled = newQuantity === 10;
    });

    this.updateSummary();
  }

  toggleDrawer() {
    if (this.drawer.classList.contains('active')) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  openDrawer() {
    this.drawer.classList.add('active');
    this.summaryToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    this.drawer.classList.remove('active');
    this.summaryToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  openProductInfoDrawer(button) {
    const productTitle = button.dataset.productTitle || '';
    const productDescription = button.dataset.productDescription || '';
    const productPrice = parseInt(button.dataset.productPrice) || 0;
    const productImage = button.dataset.productImage || '';

    // Populate drawer content
    this.productInfoTitle.textContent = productTitle;
    this.productInfoDescription.innerHTML = productDescription || '<p>Sem descrição disponível.</p>';
    this.productInfoPrice.textContent = this.formatMoney(productPrice);

    // Build image
    this.buildProductImage(productImage, productTitle);

    // Open drawer
    this.productInfoDrawer.classList.add('active');
    this.productInfoDrawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  closeProductInfoDrawer() {
    this.productInfoDrawer.classList.remove('active');
    this.productInfoDrawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Clear image
    this.productInfoSlideshow.innerHTML = '';
  }

  buildProductImage(imageUrl, altText) {
    if (!imageUrl) {
      this.productInfoSlideshow.innerHTML = `
        <div class="monte-kit__product-info-no-image">
          <span>Sem imagem disponível</span>
        </div>
      `;
      return;
    }

    this.productInfoSlideshow.innerHTML = `
      <img 
        src="${imageUrl}" 
        alt="${altText}" 
        class="monte-kit__product-info-image"
        loading="eager"
      />
    `;
  }

  showWarning(message) {
    // Implement toast notification
    console.warn(message);
  }

  showInfo(message) {
    // Update info message display
    console.info(message);
  }

  hideInfo() {
    // Hide info message
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(cents / 100);
  }

  async addToCart() {
    const items = [];
    const baseVariantId = this.addToCartBtn.dataset.baseVariantId;

    // IMPLEMENTAÇÃO DE NESTED CART LINES
    // O produto base (cesta) será o item pai
    // Todos os produtos selecionados serão aninhados sob ele

    // Buscar o valor do recado de qualquer textarea ativo
    // Verificamos tanto desktop quanto mobile
    let recadoValue = '';
    const noteTextareas = this.section.querySelectorAll('.product-note__textarea');
    noteTextareas.forEach(textarea => {
      if (!textarea.disabled && textarea.value.trim()) {
        recadoValue = textarea.value.trim();
      }
    });

    // Montar propriedades do produto base
    const baseProperties = {
      '_kit_type': 'Monte seu Kit',
      '_total_items': this.getTotalSelectedItems().toString(),
      '_kit_price': this.formatMoney(parseInt(this.totalPriceEl.dataset.total))
    };

    // Adicionar recado se existir
    if (recadoValue) {
      baseProperties['Recado'] = recadoValue;
    }

    // Adicionar produto base como item pai
    items.push({
      id: baseVariantId,
      quantity: this.totalQuantity,
      properties: baseProperties
    });

    // Adicionar produtos selecionados como itens aninhados
    // Usando parent_id para criar a relação de aninhamento
    this.selectedItems.forEach((item, index) => {
      items.push({
        id: item.variantId,
        quantity: item.quantity * this.totalQuantity,
        parent_id: baseVariantId, // Referência ao produto pai
        properties: {
          '_parent_product': 'Kit Personalizado',
          '_item_position': (index + 1).toString()
        }
      });
    });

    // Show loading state
    [this.addToCartBtn, this.addToCartMobileBtn, this.drawerAddCart].forEach(btn => {
      if (btn) {
        btn.disabled = true;
        btn.classList.add('is-loading');
        const originalText = btn.textContent;
        btn.textContent = 'Adicionando...';
        btn.dataset.originalText = originalText;
      }
    });

    try {
      // Usar a API de nested cart lines
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        // Success - redirect to cart or show success message
        const cartData = await response.json();
        console.log('Kit adicionado com sucesso com nested cart lines:', cartData);

        // Opcional: Mostrar mensagem de sucesso antes de redirecionar
        this.showSuccessMessage();

        // Aguardar um momento para mostrar a mensagem
        setTimeout(() => {
          window.location.href = '/cart';
        }, 1000);
      } else {
        const errorData = await response.json();
        console.error('Erro na resposta:', errorData);
        throw new Error(errorData.description || 'Failed to add items to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showWarning('Erro ao adicionar ao carrinho. Tente novamente.');

      // Reset loading state
      [this.addToCartBtn, this.addToCartMobileBtn, this.drawerAddCart].forEach(btn => {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('is-loading');
          btn.textContent = btn.dataset.originalText || 'adicionar ao carrinho';
        }
      });
    }
  }

  showSuccessMessage() {
    // Criar elemento de notificação
    const notification = document.createElement('div');
    notification.className = 'monte-kit__notification monte-kit__notification--success';
    notification.innerHTML = `
      <div class="monte-kit__notification-content">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="currentColor"/>
        </svg>
        <span>Kit adicionado ao carrinho com sucesso!</span>
      </div>
    `;

    document.body.appendChild(notification);

    // Animar entrada
    setTimeout(() => {
      notification.classList.add('monte-kit__notification--visible');
    }, 10);

    // Remover após 3 segundos
    setTimeout(() => {
      notification.classList.remove('monte-kit__notification--visible');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }
}

// CSS de animações e notificações adicionais
const style = document.createElement('style');
style.textContent = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .monte-kit__notification {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 9999;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    padding: 16px 20px;
    transform: translateX(400px);
    transition: transform 0.3s ease;
  }
  
  .monte-kit__notification--visible {
    transform: translateX(0);
  }
  
  .monte-kit__notification--success {
    border-left: 4px solid #10B981;
  }
  
  .monte-kit__notification-content {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #10B981;
    font-weight: 500;
  }
  
  .monte-kit__add-to-cart.is-loading,
  .monte-kit__add-to-cart-mobile.is-loading,
  .monte-kit__drawer-add-cart.is-loading {
    position: relative;
    opacity: 0.7;
    cursor: wait;
  }
`;
document.head.appendChild(style);

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const sections = document.querySelectorAll('.monte-kit-section');
  sections.forEach(section => {
    new MonteKitManager(section);
  });
});

// Re-initialize when section is loaded dynamically (Theme Editor)
document.addEventListener('shopify:section:load', (e) => {
  const section = e.target.querySelector('.monte-kit-section');
  if (section) {
    new MonteKitManager(section);
  }
});
